import React, { useState, useEffect } from 'react';
import { subscriptionAPI } from '../services/api';
import { AlertCircle, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';

const StaffLimitWarning = ({ restaurantId, onLimitReached }) => {
  const [limitInfo, setLimitInfo] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const checkLimits = async () => {
      if (!restaurantId) {
        if (onLimitReached) {
          onLimitReached(false);
        }
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await subscriptionAPI.checkStaffLimits(restaurantId);
        console.log('🔍 Staff Limit API Response:', response);

        const limitData = response.data || response;
        console.log('🔍 Limit Data:', limitData);

        setLimitInfo(limitData);
        setError(null);

        if (onLimitReached) {
          const shouldBlock = !limitData.canJoin;
          onLimitReached(shouldBlock);
          console.log('🔄 Setting staffLimitReached to:', shouldBlock, 'because canJoin is:', limitData.canJoin);
        }
      } catch (error) {
        console.error('❌ Error checking limits:', error);
        setError(error.response?.data || error.message || 'Failed to check restaurant limits');
        setLimitInfo(null);

        if (onLimitReached) {
          onLimitReached(false); // Don't block registration if we can't verify
        }
      } finally {
        setLoading(false);
      }
    };

    const timeoutId = setTimeout(checkLimits, 500);
    return () => clearTimeout(timeoutId);
  }, [restaurantId, onLimitReached]);

  if (loading) {
    return (
      <div className="mt-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
        <div className="flex items-center gap-2 text-blue-700">
          <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          <span className="text-sm">Checking restaurant limits...</span>
        </div>
      </div>
    );
  }

  // Show error message (including restaurant not found)
  if (error) {
    return (
      <div className="mt-2 p-3 bg-red-50 rounded-lg border border-red-200">
        <div className="flex items-start gap-2">
          <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-red-800">Verification Error</p>
            <p className="text-sm text-red-700 mt-1">
              {error.message || error || 'Restaurant not found or unavailable'}
            </p>
            <p className="text-sm text-red-600 mt-2">
              Please check the restaurant ID and try again.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!limitInfo) return null;

  // Block registration
  if (limitInfo.canJoin === false) {
    return (
      <div className="mt-2 p-3 bg-red-50 rounded-lg border border-red-200">
        <div className="flex items-start gap-2">
          <XCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-red-800">Registration Blocked</p>
            <p className="text-sm text-red-700 mt-1">
              {limitInfo.message}
            </p>
            <p className="text-sm text-red-600 mt-2">
              <strong>You cannot join this restaurant at this time.</strong>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Trial active but limit reached - show warning but allow
  if (limitInfo.canJoin === true && limitInfo.reachedLimit === true && limitInfo.isTrialActive === true) {
    return (
      <div className="mt-2 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
        <div className="flex items-start gap-2">
          <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-yellow-800">Trial Period - Registration Allowed</p>
            <p className="text-sm text-yellow-700 mt-1">
              {limitInfo.message}
            </p>
            <p className="text-sm text-yellow-600 mt-2">
              <strong>You can join during the trial period, but the restaurant has reached its staff limit.</strong>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Allowed to join
  return (
    <div className="mt-2 p-3 bg-green-50 rounded-lg border border-green-200">
      <div className="flex items-start gap-2">
        <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <p className="text-sm font-medium text-green-800">Can Join Restaurant</p>
          <p className="text-sm text-green-700 mt-1">
            {limitInfo.message || 'You can join this restaurant.'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default StaffLimitWarning;